import { useEffect } from 'react';

// Constants for header heights and scroll margins
const SCROLL_CONSTANTS = {
  header: {
    mobile: 72,
    desktop: 120,
    mobileSubnav: 72,
    desktopSubnav: 120,
  },
  margins: {
    default: 20,
    knowledgeIntelligence: {
      mobile: 144,
      desktop: 200,
    },
    documentation: 20,
    artificialIntelligence: {
      mobile: 120,
      desktop: 160,
    }
  }
} as const;

// Types for scroll options
export interface ScrollOptions {
  smooth?: boolean;
  offset?: number;
  behavior?: ScrollBehavior;
  delay?: number;
  preserveExisting?: boolean;
  isFooterLink?: boolean;
}

// Helper to get current header height based on viewport
const getHeaderHeight = (isMobile: boolean, isSubnav: boolean = false): number => {
  // Check if announcement banner is visible and add its height
  const hasAnnouncement = document.body.classList.contains('has-announcement');
  const announcementHeight = hasAnnouncement ? (isMobile ? 58 : 36) : 0;
  
  if (isSubnav) {
    return (isMobile ? SCROLL_CONSTANTS.header.mobileSubnav : SCROLL_CONSTANTS.header.desktopSubnav) + announcementHeight;
  }
  return (isMobile ? SCROLL_CONSTANTS.header.mobile : SCROLL_CONSTANTS.header.desktop) + announcementHeight;
};

// Helper to check if we're on mobile
const isMobile = (): boolean => window.innerWidth < 768;

// Helper to determine page type and get appropriate scroll margin
const getScrollMargin = (path: string, elementId: string | null): number => {
  if (!elementId) return 0;
  
  if (path === '/knowledge-intelligence') {
    const isMobileView = isMobile();
    return isMobileView 
      ? SCROLL_CONSTANTS.margins.knowledgeIntelligence.mobile 
      : SCROLL_CONSTANTS.margins.knowledgeIntelligence.desktop;
  }
  
  if (path === '/' && elementId.startsWith('how-it-works')) {
    const isMobileView = isMobile();
    return isMobileView 
      ? SCROLL_CONSTANTS.margins.artificialIntelligence.mobile 
      : SCROLL_CONSTANTS.margins.artificialIntelligence.desktop;
  }
  
  if (['/terms', '/privacy', '/cookies', '/faq'].includes(path)) {
    return SCROLL_CONSTANTS.margins.documentation;
  }
  
  return SCROLL_CONSTANTS.margins.default;
};

// Main scroll function that handles all scroll cases
export const scrollToElement = (elementId: string, options: ScrollOptions = {}) => {
  const element = document.getElementById(elementId);
  if (!element) return;

  const {
    smooth = true,
    offset = 0,
    behavior = 'auto',
    delay = 0,
    preserveExisting = false,
    isFooterLink = false
  } = options;

  // If preserveExisting is true and the element has its own scroll handling,
  // don't override it
  if (preserveExisting && element.hasAttribute('data-scroll-handled')) {
    return;
  }

  const isMobileView = isMobile();
  const headerHeight = getHeaderHeight(isMobileView);
  const spacing = 48; // Additional padding below navigation
  
  // For documentation pages (terms, privacy, cookies, etc.), handle special cases
  if (['terms', 'privacy', 'cookies', 'faq', 'about', 'glossary', 'contact'].includes(elementId)) {
    // If it's a footer link, scroll to top
    if (isFooterLink) {
      setTimeout(() => {
        window.scrollTo({
          top: 0,
          behavior: smooth ? behavior : 'auto'
        });
      }, delay);
      return;
    }

    // Otherwise, find the heading and scroll to it
    const heading = element.querySelector('h1, h2, h3');
    if (heading) {
      const headingPosition = heading.getBoundingClientRect().top + window.pageYOffset;
      const totalOffset = headerHeight + spacing;
      
      setTimeout(() => {
        window.scrollTo({
          top: headingPosition - totalOffset,
          behavior: smooth ? behavior : 'auto'
        });
      }, delay);
      return;
    }
  }
  
  // For artificial intelligence sections, chat with data, and revenue teams, find the heading within the section
  if (
    elementId.startsWith('how-it-works') || 
    elementId === 'chat-with-data' ||
    elementId === 'revenue-intelligence'
  ) {
    // Find the first h2 or h3 heading within the section
    const heading = element.querySelector('h2, h3');
    if (heading) {
      const headingPosition = heading.getBoundingClientRect().top + window.pageYOffset;
      
      // Position the heading just below the header with spacing
      const targetPosition = headingPosition - headerHeight - spacing;
      
      setTimeout(() => {
        window.scrollTo({
          top: targetPosition,
          behavior: smooth ? behavior : 'auto'
        });
      }, delay);
      return;
    }
  }
  
  // For product management section, target the h1 heading
  if (elementId === 'product-management') {
    const heading = element.querySelector('h1');
    if (heading) {
      const headingPosition = heading.getBoundingClientRect().top + window.pageYOffset;
      
      // Position the heading just below the header with spacing
      const targetPosition = headingPosition - headerHeight - spacing;
      
      setTimeout(() => {
        window.scrollTo({
          top: targetPosition,
          behavior: smooth ? behavior : 'auto'
        });
      }, delay);
      return;
    }
  }
  
  // For workflow jobs section, target the header within the section
  if (elementId === 'workflow-jobs') {
    const sectionHeader = element.querySelector('h1');
    if (sectionHeader) {
      const headerPosition = sectionHeader.getBoundingClientRect().top + window.pageYOffset;
      const totalOffset = headerHeight + spacing;
      
      setTimeout(() => {
        window.scrollTo({
          top: headerPosition - totalOffset,
          behavior: smooth ? behavior : 'auto'
        });
      }, delay);
      return;
    }
  }
  
  // For other sections, use the standard scroll behavior
  const subnavHeight = 72; // Height of the subnav bar
  const totalOffset = headerHeight + subnavHeight + spacing;
  const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
  
  setTimeout(() => {
    if (isFooterLink) {
      // For footer links, scroll to the bottom of the section
      const sectionBottom = elementPosition + element.offsetHeight - window.innerHeight;
      window.scrollTo({
        top: sectionBottom,
        behavior: smooth ? behavior : 'auto'
      });
    } else {
      // For non-footer links, scroll to the top of the section with additional padding
      window.scrollTo({
        top: elementPosition - totalOffset,
        behavior: smooth ? behavior : 'auto'
      });
    }
  }, delay);
};

// Function to handle navigation with scroll
export const handleNavigationScroll = (path: string, elementId?: string) => {
  const navigate = (window as any).__navigate;
  const currentPath = window.location.pathname;
  const targetPath = path.split('#')[0];
  const isFooterLink = document.activeElement?.closest('footer') !== null;

  // If we're already on the page, handle scrolling directly
  if (currentPath === targetPath) {
    if (elementId) {
      scrollToElement(elementId, { isFooterLink });
    } else {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
    return;
  }

  // For cross-page navigation
  if (navigate) {
    // For footer links, add special state and use localStorage for early access
    if (isFooterLink) {
      // Inject script into HTML to run immediately on page load before any JS
      const injectScript = `
        <script id="scroll-script">
          // Run immediately before page renders
          document.documentElement.style.scrollBehavior = 'auto';
          window.scrollTo(0, 999999);
          
          // Add a class to body to start at bottom
          document.body.classList.add('start-at-bottom');
          
          // Store data for React
          localStorage.setItem('startFromBottom', 'true');
          localStorage.setItem('scrollTarget', '${elementId || 'top'}');
          
          // Remove script after execution
          setTimeout(function() {
            var script = document.getElementById('scroll-script');
            if (script) script.parentNode.removeChild(script);
          }, 0);
        </script>
      `;
      
      // Add style for start-at-bottom class
      const styleTag = document.createElement('style');
      styleTag.innerHTML = `
        .start-at-bottom {
          height: 100%;
          scroll-padding-bottom: 0;
        }
      `;
      document.head.appendChild(styleTag);
      
      // Add the script to localStorage so it can be retrieved and executed before React hydration
      localStorage.setItem('injectScript', injectScript);
      
      // Then navigate with state
      navigate(path, { 
        state: { 
          fromFooter: true,
          scrollTo: elementId || 'top'
        },
        replace: true
      });
    } else {
      // Clear any existing footer navigation flags
      localStorage.removeItem('startFromBottom');
      localStorage.removeItem('scrollTarget');
      localStorage.removeItem('injectScript');
      
      // For non-footer links, use normal navigation
      navigate(path, elementId ? { state: { scrollTo: elementId } } : undefined);
    }
  } else {
    // For non-SPA navigation
    if (isFooterLink) {
      // Set flags in localStorage before navigation
      localStorage.setItem('startFromBottom', 'true');
      localStorage.setItem('scrollTarget', elementId || 'top');
    } else {
      localStorage.removeItem('startFromBottom');
      localStorage.removeItem('scrollTarget');
    }
    window.location.href = path;
  }
};

// Hook to handle scroll restoration and initial scroll on page load
export const useScrollHandler = (elementId?: string | null, options: ScrollOptions = {}) => {
  useEffect(() => {
    // Check if we have an inject script and execute it
    const injectScript = localStorage.getItem('injectScript');
    if (injectScript) {
      // Clear it immediately
      localStorage.removeItem('injectScript');
      
      // Add it to the DOM (it will self-destruct)
      const div = document.createElement('div');
      div.innerHTML = injectScript;
      document.body.appendChild(div);
    }
    
    // Prevent default scroll restoration
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }
    
    // Check for localStorage flags (these are set before navigation happens)
    const startFromBottom = localStorage.getItem('startFromBottom') === 'true';
    const scrollTarget = localStorage.getItem('scrollTarget');
    
    // Get the hash and state
    const hash = window.location.hash;
    const state = (window.history.state as any)?.usr;
    
    // Clear localStorage flags immediately after reading them
    if (startFromBottom) {
      localStorage.removeItem('startFromBottom');
      localStorage.removeItem('scrollTarget');
    }

    // Function to handle scroll up after starting from bottom
    const scrollUp = () => {
      // Remove start-at-bottom class
      document.body.classList.remove('start-at-bottom');
      
      // Reset scroll behavior
      document.documentElement.style.scrollBehavior = '';
      
      const target = scrollTarget || (state?.scrollTo || 'top');
      if (target === 'top') {
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      } else {
        scrollToElement(target, { ...options, delay: 0 });
      }
    };

    // Handle scroll based on navigation type
    if (startFromBottom || state?.fromFooter) {
      // For footer navigation - we're already at the bottom due to the script
      // Just need to scroll up after a short delay
      setTimeout(scrollUp, 100);
    } else {
      // Handle normal scroll cases
      const handleNormalScroll = () => {
        if (hash) {
          const targetId = hash.replace('#', '');
          scrollToElement(targetId, { ...options, delay: 0 });
        } else if (state?.scrollTo) {
          // If we have a tab action, handle it first
          if (state.tabAction && state.scrollTo === 'workflow-jobs') {
            const tabId = state.tabAction === 'templates' ? 'templates-tab' : 'custom-tab';
            handleTabScroll(tabId, state.scrollTo);
          } else {
            scrollToElement(state.scrollTo, { ...options, delay: 0 });
          }
        } else {
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
        }
      };
      
      // Use a small delay for normal scroll cases
      setTimeout(handleNormalScroll, 100);
    }
  }, [elementId]);
};

// Function to handle tab changes with scroll
export const handleTabScroll = (
  tabId: string,
  elementId: string | null,
  options: ScrollOptions = {}
): void => {
  const tabButton = document.getElementById(tabId);
  if (tabButton && tabButton instanceof HTMLElement) {
    tabButton.click();
    
    // Special handling for workflow tabs
    if (elementId === 'workflow-jobs') {
      const element = document.getElementById(elementId);
      if (element) {
        const isMobileView = window.innerWidth < 768;
        const headerHeight = getHeaderHeight(isMobileView);
        const subnavHeight = 72;
        const spacing = 48;
        
        const sectionHeader = element.querySelector('h1');
        if (sectionHeader) {
          const headerPosition = sectionHeader.getBoundingClientRect().top + window.pageYOffset;
          const totalOffset = headerHeight + subnavHeight + spacing;
          
          setTimeout(() => {
            window.scrollTo({
              top: headerPosition - totalOffset,
              behavior: 'smooth'
            });
          }, 300);
          return;
        }
      }
    }
    
    // Default behavior for other tabs
    scrollToElement(elementId, { ...options, delay: 300, preserveExisting: true });
  }
}; 